import * as React from 'react';
import Button from '@mui/material-next/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import PropTypes from "prop-types";
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from "react-i18next";
import Stack from '@mui/material/Stack';
import { useNotifications } from "../../../hooks/notification/notificationHook";
import PromiseBuilder from "../../../utils/PromiseUtils";
import ApiClient from "../../../api/ApiClient";
import * as Yup from "yup";
import { Formik, Form } from 'formik'
import BlurryDialog from '../../../widget/BlurryDialog';
import { IconButton, TextField } from '@mui/material';


const CampaignInviteDialog = (props) => {
  const { t } = useTranslation();
  const { addSuccessNotification, addErrorNotification } = useNotifications();

  const descriptionElementRef = React.useRef(null);

  const CampaignInviteSchema = Yup.object().shape({
    newCampaignId: Yup.string().required(t('validation.required'))
      .min(36, t('validation.idLength'))
      .max(36, t('validation.idLength')),
    url: Yup.string().required(t('validation.required'))
  });

  const inviteToCampaign = (form, resetForm) => {
    props.setIsLoading(true);
    const payload = {
      newCampaignId: form.newCampaignId,
      inviteLink: form.url,
      campaignIds: props.selectedCampaigns
    };
    new PromiseBuilder()
      .then(async () => ApiClient.post("/api/v1/campaign/email/invite", payload))
      .then(async (res) => {
        props.setIsLoading(false);
        addSuccessNotification(t("toast.emailSent"), t("toast.success"));
        closeDialog(resetForm, true)
      })
      .catch(async (e) => {
        props.setIsLoading(false);
        addErrorNotification(t("toast.fetchCandidatesFailed"), t("toast.error"));
        console.log("ERROR", e);
      })
      .run();
  }

  React.useEffect(() => {
    if (props.open) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [props.open]);

  const closeDialog = (resetForm, uncheck) => {
    resetForm();
    props.handleClose(uncheck);
  }

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{ newCampaignId: '', url: '', campaigns: props.selectedCampaigns }}
      validationSchema={CampaignInviteSchema}
      onSubmit={(values, { resetForm }) => {
        inviteToCampaign(values, resetForm);

      }}
    >
      {({ handleSubmit, handleChange, values, resetForm, errors, isValid, dirty }) => (
        <Form>
          <BlurryDialog
            open={props.open}
            onClose={() => closeDialog(resetForm, false)}
          >
            <DialogTitle
              id="scroll-dialog-title"
              className="color color-medium-purple">
              <b>
                {t("campaign.inviteToCampaign")}
              </b>
            </DialogTitle>
            <IconButton
              aria-label="close"
              onClick={() => closeDialog(resetForm, false)}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent>
              <DialogContentText
                id="scroll-invite-dialog-description"
                ref={descriptionElementRef}
                tabIndex={-1}
              >
                <Stack spacing={3}>
                <TextField
                    id="dialog-campaign-id"
                    name={'newCampaignId'}
                    value={values.newCampaignId}
                    error={errors.newCampaignId}
                    helperText={errors.newCampaignId}
                    onChange={handleChange}
                    label={t("campaign.newCampaignId")}
                    variant="outlined"
                    style={{ marginBottom: '15px' }}
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px"
                      }
                    }}
                  />
                  <TextField
                    id="dialog-campaign-url"
                    name={'url'}
                    value={values.url}
                    error={errors.url}
                    helperText={errors.url}
                    onChange={handleChange}
                    label={t("campaign.url")}
                    variant="outlined"
                    style={{ marginBottom: '15px' }}
                    size="small"
                    required
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px"
                      }
                    }}
                  />
                </Stack>
              </DialogContentText>
            </DialogContent>
            <DialogContent>
              <DialogActions>
                <Button
                  variant="filled"
                  disableElevation
                  disabled={!isValid || !dirty}
                  className="bg bg-medium-purple"
                  type="submit"
                  size="small"
                  style={{ borderRadius: '10px' }}
                  onClick={handleSubmit}
                >
                  {t("campaign.invite")}
                </Button>
              </DialogActions>
            </DialogContent>
          </BlurryDialog>
        </Form>
      )}
    </Formik>
  );
}

CampaignInviteDialog.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleSave: PropTypes.func,
  selectedCampaigns: PropTypes.array,
  setIsLoading: PropTypes.func
};

export default CampaignInviteDialog;